import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import TutorCard from "@/components/TutorCard";
import TutorFilters from "@/components/TutorFilters";
import SessionRequestModal from "@/components/SessionRequestModal";
import { api } from "@/lib/api";

export default function TutorsBrowse() {
  const [searchParams] = useSearchParams();
  const initialSubject = searchParams.get("subject") || "All";

  const [filters, setFilters] = useState({
    subject: initialSubject,
    level: "All",
    q: "",
    priceRange: [15, 120],
    sort: "rating",
  });
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const params = useMemo(() => {
    const p = {};
    if (filters.subject && filters.subject !== "All") p.subject = filters.subject;
    if (filters.level && filters.level !== "All") p.level = filters.level;
    if (filters.q) p.q = filters.q;
    if (filters.priceRange) {
      p.min_price = filters.priceRange[0];
      p.max_price = filters.priceRange[1];
    }
    if (filters.sort) p.sort = filters.sort;
    return p;
  }, [filters]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/tutors", { params });
        if (active) setTutors(data.tutors);
      } finally {
        if (active) setLoading(false);
      }
    }, 220);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [params]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="mb-8 max-w-2xl">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
          Find your tutor or guide
        </h1>
        <p className="mt-3 text-muted-foreground">
          Browse hand-picked <span className="font-medium text-foreground">Language & Math Guides</span> and
          verified independent tutors across French, English, Spanish and Math. Filter by subject,
          level and price to find your match.
        </p>
      </div>

      <TutorFilters filters={filters} onChange={setFilters} />

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-muted-foreground" data-testid="tutors-count">
          {loading ? "Searching…" : `${tutors.length} tutor${tutors.length === 1 ? "" : "s"} available`}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : tutors.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center mt-6" data-testid="empty-state">
          <p className="font-semibold text-lg">No tutors match your filters</p>
          <p className="text-muted-foreground mt-1">Try widening the price range or clearing the subject.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {tutors.map((t) => (
            <TutorCard key={t.id} tutor={t} onBook={setSelected} />
          ))}
        </div>
      )}

      {selected && (
        <SessionRequestModal
          tutor={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
        />
      )}
    </div>
  );
}
